import { useEffect, useState, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { uploadFile, getDownloadUrl, deleteFile } from '../../r2Client'
import {
  Upload, Trash2, Download, File, Image, Film, Music,
  Archive, FileText, Search, X, ChevronDown, History,
  ChevronRight, Maximize2, FolderOpen, Folder,
  FolderPlus, ArrowLeft, FolderInput, Check,
  Info, HardDrive, AlertTriangle, TrendingUp,
  Link2, MessageSquare, Lock, Users,
} from 'lucide-react'

/* ─── helpers ─── */
const fmt = (bytes) => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const tipoIcon = (tipo) => {
  if (!tipo) return <File size={18} className="text-gray-500" />
  if (tipo.startsWith('image/')) return <Image size={18} className="text-violet-400" />
  if (tipo.startsWith('video/')) return <Film size={18} className="text-blue-400" />
  if (tipo.startsWith('audio/')) return <Music size={18} className="text-emerald-400" />
  if (tipo.includes('pdf') || tipo.includes('word') || tipo.includes('text')) return <FileText size={18} className="text-amber-400" />
  if (tipo.includes('zip') || tipo.includes('rar') || tipo.includes('tar')) return <Archive size={18} className="text-rose-400" />
  return <File size={18} className="text-gray-500" />
}

/* Ricava il path completo di una cartella risalendo i parent */
const buildFolderPath = (cartella, allCartelle) => {
  const path = []
  let cur = cartella
  while (cur) {
    path.unshift({ id: cur.id, nome: cur.nome, project_id: cur.project_id })
    const parentId = cur.parent_id
    cur = allCartelle.find(c => c.id === parentId) || null
  }
  return path
}

const getFolderFullPath = (cartella, allCartelle) =>
  buildFolderPath(cartella, allCartelle).map(f => f.nome).join('/')

/* ─── ProgressBar ─── */
const ProgressBar = ({ items }) => {
  if (!items.length) return null
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 mb-4 space-y-2">
      {items.map(item => (
        <div key={item.name}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-300 truncate max-w-xs">{item.name}</span>
            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{item.progress}%</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-1.5">
            <div className="bg-violet-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${item.progress}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ─── VersioniPanel ─── */
const VersioniPanel = ({ file, onClose, onDownload }) => {
  const [versioni, setVersioni] = useState([])
  useEffect(() => {
    const rootId = file.file_padre_id || file.id
    supabase.from('file_uploads')
      .select('*')
      .or(`id.eq.${rootId},file_padre_id.eq.${rootId}`)
      .order('versione', { ascending: false })
      .then(({ data }) => setVersioni(data || []))
  }, [file])
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Cronologia versioni</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-gray-500 text-sm mb-4 truncate">{file.nome}</p>
        <div className="space-y-2">
          {versioni.map((v, i) => (
            <div key={v.id} className="flex items-center gap-3 p-3 bg-gray-800 rounded-xl">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-violet-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                v{v.versione}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{v.nome}</p>
                <p className="text-gray-500 text-xs">{fmt(v.dimensione)} · {new Date(v.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              </div>
              {i === 0 && <span className="text-xs text-violet-400 flex-shrink-0">Ultima</span>}
              <button onClick={() => onDownload(v)} className="p-1.5 text-gray-500 hover:text-violet-400 transition-colors flex-shrink-0">
                <Download size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ─── PreviewModal ─── */
const PreviewModal = ({ file, onClose, onDownload }) => {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!file?.r2_key) return
    getDownloadUrl(file.r2_key).then(setUrl)
  }, [file])
  const isImage = file?.tipo?.startsWith('image/')
  const isVideo = file?.tipo?.startsWith('video/')
  const isAudio = file?.tipo?.startsWith('audio/')
  const isPdf   = file?.tipo?.includes('pdf')
  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden w-full max-w-5xl flex flex-col" style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {tipoIcon(file?.tipo)}
            <p className="text-white font-medium truncate text-sm">{file?.nome}</p>
            {file?.versione > 1 && <span className="text-xs bg-violet-600/20 text-violet-400 px-1.5 py-0.5 rounded flex-shrink-0">v{file.versione}</span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button onClick={() => onDownload(file)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg transition-colors">
              <Download size={13} /> Scarica
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center p-6 min-h-0 bg-gray-950">
          {!url ? (
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          ) : isImage ? (
            <img src={url} alt={file.nome} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
          ) : isVideo ? (
            <video src={url} controls autoPlay className="max-w-full max-h-full rounded-xl shadow-2xl" style={{ maxHeight: '75vh' }} />
          ) : isAudio ? (
            <div className="text-center">
              <Music size={48} className="text-emerald-400 mx-auto mb-6" />
              <p className="text-gray-300 text-sm mb-4">{file.nome}</p>
              <audio src={url} controls className="w-80" autoPlay />
            </div>
          ) : isPdf ? (
            <iframe src={url} title={file.nome} className="w-full rounded-xl border border-gray-800" style={{ height: '75vh' }} />
          ) : (
            <div className="text-center py-12">
              <File size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-sm mb-6">Anteprima non disponibile per questo tipo di file.</p>
              <button onClick={() => onDownload(file)} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors mx-auto">
                <Download size={15} /> Scarica il file
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── FileNoteModal ─── */
const FileNoteModal = ({ file, onClose, onSave }) => {
  const [note, setNote] = useState(file.note || '')
  const [saving, setSaving] = useState(false)
  const handleSave = async () => {
    setSaving(true)
    await onSave(file.id, note.trim() || null)
    onClose()
  }
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare size={15} className="text-violet-400 flex-shrink-0" />
            <h2 className="text-white font-semibold text-sm truncate">{file.nome}</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white flex-shrink-0 ml-2"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          <textarea
            autoFocus
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Aggiungi una nota a questo file..."
            rows={5}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-violet-500 placeholder-gray-600 transition-colors"
          />
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white transition-colors">Annulla</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
            {saving
              ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <Check size={14} />}
            Salva nota
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── UploadModal ─── */
const UploadModal = ({ files: initialFiles, progetti, cartelleDB, defaultCartella, onClose, onUpload }) => {
  const { profile } = useAuth()
  const [selectedProject, setSelectedProject] = useState('')
  const [cartella, setCartella] = useState(defaultCartella || '')
  const [nuovaCartella, setNuovaCartella] = useState(false)
  const [nomeNuovaCartella, setNomeNuovaCartella] = useState('')
  const [showCartelleMenu, setShowCartelleMenu] = useState(false)
  // visibilita usata solo quando NON c'è un progetto
  const [condivisione, setCondivisione] = useState('personale') // 'personale' | 'specifici'
  const [sharedWith, setSharedWith] = useState([]) // array di user IDs
  const [utenti, setUtenti] = useState([]) // lista utenti per il picker
  const menuRef = useRef()

  // Carica utenti quando si seleziona 'specifici'
  useEffect(() => {
    if (condivisione === 'specifici' && utenti.length === 0) {
      supabase.from('profiles')
        .select('id, full_name, nickname, avatar_url')
        .neq('id', profile?.id)
        .order('full_name')
        .then(({ data }) => setUtenti(data || []))
    }
  }, [condivisione, profile?.id, utenti.length])

  const toggleUser = (uid) =>
    setSharedWith(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid])

  // Cartelle disponibili: tutte quelle del progetto + full path
  const cartelleDisponibili = cartelleDB
    .map(c => ({ ...c, fullPath: getFolderFullPath(c, cartelleDB) }))
    .filter(c => !selectedProject || c.project_id === selectedProject || !c.project_id)
    .sort((a, b) => a.fullPath.localeCompare(b.fullPath, 'it'))

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowCartelleMenu(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const cartellaFinale = nuovaCartella ? nomeNuovaCartella.trim() : cartella

  const handleConfirm = () => {
    const vis = selectedProject ? 'condiviso' : condivisione
    const sw = (!selectedProject && condivisione === 'specifici') ? sharedWith : []
    onUpload(initialFiles, selectedProject || null, cartellaFinale || null, vis, sw)
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-white font-semibold">Carica file</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-800/60 rounded-xl p-3 space-y-1.5 max-h-36 overflow-y-auto">
            {Array.from(initialFiles).map((f, i) => (
              <div key={i} className="flex items-center gap-2.5">
                {tipoIcon(f.type)}
                <span className="text-sm text-gray-300 truncate flex-1">{f.name}</span>
                <span className="text-xs text-gray-600 flex-shrink-0">{fmt(f.size)}</span>
              </div>
            ))}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Progetto</label>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors appearance-none">
              <option value="">— Nessun progetto (generale) —</option>
              {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-gray-500">Cartella</label>
              <button onClick={() => { setNuovaCartella(v => !v); setCartella(''); setNomeNuovaCartella('') }}
                className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors">
                <FolderPlus size={12} />
                {nuovaCartella ? 'Usa esistente' : 'Nuova cartella'}
              </button>
            </div>
            {nuovaCartella ? (
              <input autoFocus value={nomeNuovaCartella} onChange={e => setNomeNuovaCartella(e.target.value)}
                placeholder="Nome nuova cartella..."
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors placeholder-gray-600" />
            ) : (
              <div className="relative" ref={menuRef}>
                <button onClick={() => setShowCartelleMenu(v => !v)}
                  className="w-full flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-left hover:border-gray-600 focus:outline-none focus:border-violet-500">
                  <span className={cartella ? 'text-white flex items-center gap-2' : 'text-gray-600'}>
                    {cartella ? <><Folder size={13} className="text-amber-400" /> {cartella}</> : '— Radice (nessuna cartella) —'}
                  </span>
                  <ChevronDown size={14} className="text-gray-500 flex-shrink-0" />
                </button>
                {showCartelleMenu && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden z-20 shadow-xl max-h-52 overflow-y-auto">
                    <button onClick={() => { setCartella(''); setShowCartelleMenu(false) }}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left ${!cartella ? 'bg-violet-600/20 text-violet-300' : 'text-gray-400 hover:bg-gray-800'}`}>
                      <Folder size={13} className="text-gray-600" /> Radice
                    </button>
                    {cartelleDisponibili.map(c => (
                      <button key={c.id} onClick={() => { setCartella(c.fullPath); setShowCartelleMenu(false) }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left ${cartella === c.fullPath ? 'bg-violet-600/20 text-violet-300' : 'text-gray-300 hover:bg-gray-800'}`}>
                        <Folder size={13} className="text-amber-400" />
                        <span className="truncate">{c.fullPath}</span>
                      </button>
                    ))}
                    {cartelleDisponibili.length === 0 && (
                      <p className="px-4 py-3 text-xs text-gray-600">Nessuna cartella ancora.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Visibilità */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Visibilità</label>
            {selectedProject ? (
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-gray-400">
                <Users size={14} className="text-violet-400 flex-shrink-0" />
                <span>Condiviso con tutto il team del progetto</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setCondivisione('personale')}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-medium transition-colors ${condivisione === 'personale' ? 'bg-violet-600/20 border-violet-500 text-violet-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}
                  >
                    <Lock size={16} />
                    <span>Solo io</span>
                    <span className="text-xs font-normal opacity-60">Privato</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCondivisione('specifici')}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-medium transition-colors ${condivisione === 'specifici' ? 'bg-violet-600/20 border-violet-500 text-violet-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}
                  >
                    <Users size={16} />
                    <span>Condividi</span>
                    <span className="text-xs font-normal opacity-60">Scegli persone</span>
                  </button>
                </div>
                {condivisione === 'specifici' && (
                  <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden max-h-36 overflow-y-auto">
                    {utenti.length === 0 ? (
                      <p className="text-xs text-gray-600 px-4 py-3">Caricamento...</p>
                    ) : utenti.map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleUser(u.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-700 transition-colors text-left"
                      >
                        <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${sharedWith.includes(u.id) ? 'bg-violet-600 border-violet-600' : 'border-gray-600'}`}>
                          {sharedWith.includes(u.id) && <Check size={10} className="text-white" />}
                        </div>
                        <span className="text-sm text-gray-300 truncate">{u.nickname || u.full_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white transition-colors">Annulla</button>
          <button onClick={handleConfirm} disabled={nuovaCartella && !nomeNuovaCartella.trim()}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
            <Upload size={14} />
            Carica {initialFiles.length > 1 ? `${initialFiles.length} file` : 'file'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── NuovaCartellaModal ─── */
const NuovaCartellaModal = ({ progetti, defaultProject, parentId, parentNome, onClose, onCreate }) => {
  const [nome, setNome] = useState('')
  const [projectId, setProjectId] = useState(defaultProject !== 'all' ? (defaultProject || '') : '')
  const [loading, setLoading] = useState(false)

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!nome.trim()) return
    setLoading(true)
    await onCreate(nome.trim(), projectId || null, parentId || null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <FolderPlus size={16} className="text-amber-400" />
            {parentNome ? `Sottocartella in "${parentNome}"` : 'Nuova cartella'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
          {parentNome && (
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
              <Folder size={13} className="text-amber-400 flex-shrink-0" />
              <span className="text-xs text-amber-300">{parentNome}</span>
              <ChevronRight size={11} className="text-gray-600" />
              <span className="text-xs text-white">{nome || '...'}</span>
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Nome cartella</label>
            <input autoFocus value={nome} onChange={e => setNome(e.target.value)}
              placeholder="es. Raw footage, Grafiche..."
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors placeholder-gray-600" />
          </div>
          {!parentNome && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Progetto</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors appearance-none">
                <option value="">— Nessun progetto (generale) —</option>
                {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={onClose} className="text-sm text-gray-400 hover:text-white transition-colors">Annulla</button>
            <button type="submit" disabled={loading || !nome.trim()}
              className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
              {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <FolderPlus size={14} />}
              Crea cartella
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ─── SpostaModal ─── */
const SpostaModal = ({ file, progetti, cartelleDB, onClose, onSposta }) => {
  const [selectedProject, setSelectedProject] = useState(file.project_id || '')
  const [cartella, setCartella] = useState(file.cartella || '')
  const [loading, setLoading] = useState(false)

  const cartelleDisponibili = cartelleDB
    .map(c => ({ ...c, fullPath: getFolderFullPath(c, cartelleDB) }))
    .sort((a, b) => a.fullPath.localeCompare(b.fullPath, 'it'))

  const handleConfirm = async () => {
    setLoading(true)
    await onSposta(file, selectedProject || null, cartella || null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-white font-semibold">Sposta file</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-800/60 rounded-xl px-4 py-3 flex items-center gap-3">
            {tipoIcon(file.tipo)}
            <span className="text-sm text-gray-300 truncate">{file.nome}</span>
          </div>
          <div className="text-xs text-gray-600 flex items-center gap-1.5">
            <span>Ora in:</span>
            <span className="text-gray-500">{file.progetto?.titolo || 'Generale'}</span>
            {file.cartella && <><span>/</span><span className="text-amber-600">{file.cartella}</span></>}
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Progetto destinazione</label>
            <select value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setCartella('') }}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors appearance-none">
              <option value="">— Nessun progetto (generale) —</option>
              {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Cartella destinazione</label>
            <select value={cartella} onChange={e => setCartella(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors appearance-none">
              <option value="">— Radice (nessuna cartella) —</option>
              {cartelleDisponibili.map(c => <option key={c.id} value={c.fullPath}>{c.fullPath}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white transition-colors">Annulla</button>
          <button onClick={handleConfirm} disabled={loading}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
            {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <FolderInput size={14} />}
            Sposta
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── BatchSpostaModal ─── */
const BatchSpostaModal = ({ count, progetti, cartelleDB, onClose, onSposta }) => {
  const [selectedProject, setSelectedProject] = useState('')
  const [cartella, setCartella] = useState('')
  const [loading, setLoading] = useState(false)

  const cartelleDisponibili = cartelleDB
    .map(c => ({ ...c, fullPath: getFolderFullPath(c, cartelleDB) }))
    .sort((a, b) => a.fullPath.localeCompare(b.fullPath, 'it'))

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-white font-semibold">Sposta {count} file</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Progetto destinazione</label>
            <select value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setCartella('') }}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 appearance-none">
              <option value="">— Nessun progetto (generale) —</option>
              {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Cartella destinazione</label>
            <select value={cartella} onChange={e => setCartella(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 appearance-none">
              <option value="">— Radice (nessuna cartella) —</option>
              {cartelleDisponibili.map(c => <option key={c.id} value={c.fullPath}>{c.fullPath}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white">Annulla</button>
          <button
            onClick={async () => { setLoading(true); await onSposta(selectedProject || null, cartella || null); onClose() }}
            disabled={loading}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl">
            {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <FolderInput size={14} />}
            Sposta tutti
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── SpostaCartellaModal ─── */
const SpostaCartellaModal = ({ cartellaName, progetti, currentProjectId, onClose, onSposta }) => {
  const [nuovoNome, setNuovoNome] = useState(cartellaName)
  const [projectId, setProjectId] = useState(currentProjectId || '')
  const [loading, setLoading] = useState(false)
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <FolderOpen size={16} className="text-amber-400" /> Rinomina cartella
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Nome cartella</label>
            <input autoFocus value={nuovoNome} onChange={e => setNuovoNome(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Progetto</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 appearance-none">
              <option value="">— Nessun progetto (generale) —</option>
              {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
            </select>
          </div>
          <p className="text-xs text-gray-600">Tutti i file nella cartella verranno aggiornati.</p>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white">Annulla</button>
          <button onClick={async () => { setLoading(true); await onSposta(cartellaName, nuovoNome.trim(), projectId || null); onClose() }}
            disabled={loading || !nuovoNome.trim()}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-xl">
            {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Check size={14} />}
            Salva
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── InfoTab ─── */
const FREE_BYTES = 10 * 1024 * 1024 * 1024
const PRICE_PER_GB = 0.015

const InfoTab = ({ files, canDelete, onDelete, confirmDelete }) => {
  const totalBytes = files.reduce((a, f) => a + (f.dimensione || 0), 0)
  const usedGB = totalBytes / (1024 * 1024 * 1024)
  const pct = Math.min((totalBytes / FREE_BYTES) * 100, 100)
  const overBytes = Math.max(totalBytes - FREE_BYTES, 0)
  const overGB = overBytes / (1024 * 1024 * 1024)
  const costMonth = overGB * PRICE_PER_GB
  const top = [...files].sort((a, b) => (b.dimensione || 0) - (a.dimensione || 0)).slice(0, 10)
  const barColor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#8b5cf6'

  return (
    <div className="space-y-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <HardDrive size={16} className="text-violet-400" />
          <h3 className="text-white font-semibold text-sm">Spazio occupato</h3>
        </div>
        <div className="flex items-end justify-between mb-2">
          <span className="text-3xl font-bold text-white">{fmt(totalBytes)}</span>
          <span className="text-sm text-gray-500">su 10 GB gratuiti</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-3 mb-2 overflow-hidden">
          <div className="h-3 rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span style={{ color: barColor }} className="font-medium">{pct.toFixed(1)}% utilizzato</span>
          <span className="text-gray-600">{fmt(Math.max(FREE_BYTES - totalBytes, 0))} liberi</span>
        </div>
        {pct >= 80 && (
          <div className="mt-4 flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
            <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-xs">
              {pct >= 100 ? 'Hai superato il limite gratuito.' : 'Stai avvicinandoti al limite. Libera spazio per evitare costi.'}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <p className="text-xs text-gray-500 mb-1">File totali</p>
          <p className="text-2xl font-bold text-white">{files.length}</p>
          <p className="text-xs text-gray-600 mt-1">media {files.length ? fmt(Math.round(totalBytes / files.length)) : '—'} / file</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <p className="text-xs text-gray-500 mb-1">Spazio extra (a pagamento)</p>
          <p className="text-2xl font-bold text-white">{overGB > 0 ? fmt(overBytes) : '—'}</p>
          <p className="text-xs text-gray-600 mt-1">{overGB > 0 ? `${overGB.toFixed(2)} GB oltre i 10 GB gratuiti` : 'Sei nella fascia gratuita'}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={12} className="text-gray-500" />
            <p className="text-xs text-gray-500">Costo stimato / mese</p>
          </div>
          <p className={`text-2xl font-bold ${costMonth > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
            {costMonth > 0 ? `$${costMonth.toFixed(2)}` : '$0.00'}
          </p>
          <p className="text-xs text-gray-600 mt-1">{costMonth > 0 ? `$0.015/GB × ${overGB.toFixed(2)} GB` : 'Piano gratuito R2'}</p>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <TrendingUp size={15} className="text-violet-400" /> Proiezione costi R2
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-600 border-b border-gray-800">
                <th className="pb-2 font-medium">Scenario</th>
                <th className="pb-2 font-medium text-right">Spazio</th>
                <th className="pb-2 font-medium text-right">Costo/mese</th>
                <th className="pb-2 font-medium text-right">Costo/anno</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {[
                { label: 'Piano gratuito', gb: 10 },
                { label: 'Attuale', gb: usedGB },
                { label: '+50% crescita', gb: usedGB * 1.5 },
                { label: 'Doppio', gb: usedGB * 2 },
                { label: '25 GB', gb: 25 },
                { label: '50 GB', gb: 50 },
              ].map(({ label, gb }) => {
                const cost = Math.max(gb - 10, 0) * PRICE_PER_GB
                const isCurrent = label === 'Attuale'
                return (
                  <tr key={label} className={isCurrent ? 'text-violet-300' : 'text-gray-400'}>
                    <td className="py-2.5 flex items-center gap-2">
                      {label}
                      {isCurrent && <span className="text-[10px] bg-violet-600/20 text-violet-400 px-1.5 py-0.5 rounded">ora</span>}
                    </td>
                    <td className="py-2.5 text-right">{gb.toFixed(1)} GB</td>
                    <td className="py-2.5 text-right">{cost === 0 ? <span className="text-emerald-500">gratis</span> : `$${cost.toFixed(2)}`}</td>
                    <td className="py-2.5 text-right">{cost === 0 ? <span className="text-emerald-500">gratis</span> : `$${(cost * 12).toFixed(2)}`}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-700 mt-4">Prezzi Cloudflare R2: 10 GB gratuiti + $0.015/GB/mese · Egress gratuito</p>
      </div>

      {top.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
            <Trash2 size={15} className="text-rose-400" /> File più pesanti — libera spazio
          </h3>
          <div className="space-y-2">
            {top.map((f, i) => {
              const filePct = totalBytes > 0 ? ((f.dimensione || 0) / totalBytes) * 100 : 0
              return (
                <div key={f.id} className="flex items-center gap-3 group">
                  <span className="text-xs text-gray-700 w-5 text-right flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {tipoIcon(f.tipo)}
                        <span className="text-sm text-gray-300 truncate">{f.nome}</span>
                      </div>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{fmt(f.dimensione)}</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-1">
                      <div className="bg-violet-600/60 h-1 rounded-full" style={{ width: `${filePct}%` }} />
                    </div>
                  </div>
                  {canDelete && (
                    <button onClick={() => onDelete(f)}
                      className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${confirmDelete === f.id ? 'text-red-400 bg-red-500/10' : 'text-gray-700 hover:text-red-400 hover:bg-gray-800 opacity-0 group-hover:opacity-100'}`}
                      title={confirmDelete === f.id ? 'Clicca ancora per confermare' : 'Elimina file'}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Main ─── */
const Storage = () => {
  const { profile } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const [files, setFiles] = useState([])
  const [progetti, setProgetti] = useState([])
  const [selectedProject, setSelectedProject] = useState('all')
  const [loading, setLoading] = useState(true)
  const [uploadQueue, setUploadQueue] = useState([])
  const [pendingFiles, setPendingFiles] = useState(null)
  const [query, setQuery] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [dropActive, setDropActive] = useState(false)
  const [showProjectMenu, setShowProjectMenu] = useState(false)
  const [versioniFile, setVersioniFile] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [spostaFile, setSpostaFile] = useState(null)
  const [cartelleDB, setCartelleDB] = useState([])
  const [showNuovaCartella, setShowNuovaCartella] = useState(false)
  const [selectedFileIds, setSelectedFileIds] = useState(new Set())
  const [showBatchSposta, setShowBatchSposta] = useState(false)
  const [spostaCartella, setSpostaCartella] = useState(null)
  const [dragOverFolder, setDragOverFolder] = useState(null)
  const [tab, setTab] = useState('files')
  const [selectMode, setSelectMode] = useState(false)
  const [noteFile, setNoteFile] = useState(null)
  const [copiedFolderId, setCopiedFolderId] = useState(null)

  // ── Navigazione cartelle (stack) ──
  // Ogni elemento: { id: string, nome: string, project_id: string|null }
  const [folderNav, setFolderNav] = useState([])
  const [showRootOnly, setShowRootOnly] = useState(false)

  const currentFolder = folderNav.length > 0 ? folderNav[folderNav.length - 1] : null
  const currentFolderPath = folderNav.length > 0 ? folderNav.map(f => f.nome).join('/') : null

  const inputRef = useRef()
  const menuRef = useRef()

  const isAdmin  = profile?.role === 'admin'
  const canUpload = profile?.role === 'admin' || profile?.role === 'member'
  const canDelete = profile?.role === 'admin'

  const fetchFiles = useCallback(async () => {
    // File condivisi specificamente con me
    const { data: condivisioni } = await supabase
      .from('file_condivisioni').select('file_id').eq('user_id', profile?.id)
    const sharedIds = condivisioni?.map(c => c.file_id) ?? []

    const orParts = [`uploaded_by.eq.${profile?.id}`, `visibilita.eq.condiviso`]
    if (sharedIds.length > 0) orParts.push(`id.in.(${sharedIds.join(',')})`)

    let q = supabase
      .from('file_uploads')
      .select('*, progetto:project_id(titolo, colore)')
      .is('file_padre_id', null)
      .or(orParts.join(','))
      .order('created_at', { ascending: false })
    if (selectedProject !== 'all') q = q.eq('project_id', selectedProject)

    let qc = supabase.from('storage_cartelle').select('*').order('nome')
    if (selectedProject !== 'all') qc = qc.eq('project_id', selectedProject)

    const [{ data }, { data: dbC }] = await Promise.all([q, qc])
    setFiles(data || [])
    setCartelleDB(dbC || [])
    setLoading(false)
  }, [selectedProject, profile?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.from('progetti').select('id, titolo, colore').order('titolo').then(({ data }) =>
      setProgetti(data || [])
    )
  }, [])

  useEffect(() => {
    setLoading(true)
    setFolderNav([])
    setShowRootOnly(false)
    setSelectedFileIds(new Set())
    setSelectMode(false)
    fetchFiles()
    const ch = supabase.channel('storage-r2')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'file_uploads' }, fetchFiles)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'storage_cartelle' }, fetchFiles)
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [fetchFiles])

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowProjectMenu(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Legge ?f=<id> dall'URL e naviga nella cartella corrispondente
  useEffect(() => {
    const fId = searchParams.get('f')
    if (!fId || !cartelleDB.length) return
    const cartella = cartelleDB.find(c => c.id === fId)
    if (!cartella) return
    const path = buildFolderPath(cartella, cartelleDB)
    setFolderNav(path)
    setShowRootOnly(false)
    setSearchParams(p => { p.delete('f'); return p }, { replace: true })
  }, [cartelleDB]) // eslint-disable-line

  // ── Navigazione cartelle ──
  const navigateInto = (cartellaObj) => {
    if (cartellaObj.id) {
      setFolderNav(prev => [...prev, { id: cartellaObj.id, nome: cartellaObj.nome, project_id: cartellaObj.project_id }])
    } else {
      // Cartella virtuale (solo da file, nessun record DB)
      setFolderNav([{ id: null, nome: cartellaObj.nome, project_id: null }])
    }
    setShowRootOnly(false)
    setSelectedFileIds(new Set())
    setSelectMode(false)
  }

  const navigateBack = () => {
    if (showRootOnly) { setShowRootOnly(false); return }
    setFolderNav(prev => prev.slice(0, -1))
    setSelectedFileIds(new Set())
    setSelectMode(false)
  }

  const navigateTo = (index) => {
    setFolderNav(prev => prev.slice(0, index + 1))
    setSelectedFileIds(new Set())
    setSelectMode(false)
  }

  const navigateToRoot = () => {
    setFolderNav([])
    setShowRootOnly(false)
    setSelectedFileIds(new Set())
    setSelectMode(false)
  }

  // ── Copia link cartella ──
  const handleCopyFolderLink = (cartella) => {
    if (!cartella.id) return
    const url = `${window.location.origin}/storage?f=${cartella.id}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedFolderId(cartella.id)
      setTimeout(() => setCopiedFolderId(null), 2000)
    })
  }

  // ── Upload ──
  const openUploadModal = (rawFiles) => {
    if (!rawFiles?.length || !canUpload) return
    setPendingFiles(Array.from(rawFiles))
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDropActive(false)
    openUploadModal(Array.from(e.dataTransfer.files))
  }

  const uploadFiles = async (rawFiles, projectId, cartellaName, visibilita = 'condiviso', sharedWith = []) => {
    setPendingFiles(null)
    const queue = rawFiles.map(f => ({ name: f.name, progress: 0 }))
    setUploadQueue(queue)
    setUploadError(null)

    for (let i = 0; i < rawFiles.length; i++) {
      const file = rawFiles[i]
      const ts = Date.now()
      const safeName = file.name.replace(/\s+/g, '_')
      const projectPrefix = projectId || 'generale'
      const folderPrefix = cartellaName ? `${projectPrefix}/${cartellaName}` : projectPrefix
      const r2Key = `${folderPrefix}/${ts}_${safeName}`

      try {
        const { data: existing } = await supabase
          .from('file_uploads')
          .select('id, versione, r2_key')
          .eq('nome', file.name)
          .eq('project_id', projectId ?? null)
          .is('file_padre_id', null)
          .order('versione', { ascending: false })
          .limit(1)
          .maybeSingle()

        await uploadFile(r2Key, file, (progress) => {
          setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress } : q))
        })

        const visFinale = projectId ? 'condiviso' : visibilita
        let newFileId = null

        if (existing) {
          const nuovaVersione = (existing.versione || 1) + 1
          const { data: ins } = await supabase.from('file_uploads').insert({
            nome: file.name, path: r2Key, r2_key: r2Key,
            dimensione: file.size, tipo: file.type || null,
            project_id: projectId || null, cartella: cartellaName || null,
            uploaded_by: profile.id, versione: nuovaVersione, file_padre_id: existing.id,
            visibilita: visFinale,
          }).select('id').single()
          newFileId = ins?.id
          await supabase.from('file_uploads').update({ r2_key: r2Key, versione: nuovaVersione }).eq('id', existing.id)
        } else {
          const { data: ins } = await supabase.from('file_uploads').insert({
            nome: file.name, path: r2Key, r2_key: r2Key,
            dimensione: file.size, tipo: file.type || null,
            project_id: projectId || null, cartella: cartellaName || null,
            uploaded_by: profile.id, versione: 1,
            visibilita: visFinale,
          }).select('id').single()
          newFileId = ins?.id
        }

        // Salva condivisioni specifiche
        if (newFileId && visFinale === 'specifici' && sharedWith.length > 0) {
          await supabase.from('file_condivisioni').insert(
            sharedWith.map(uid => ({ file_id: newFileId, user_id: uid }))
          )
        }
      } catch (err) {
        console.error('Upload error:', err)
        setUploadError(`Errore upload "${file.name}": ${err.message}`)
      }
    }
    setUploadQueue([])
    fetchFiles()
  }

  const handleDownload = async (file) => {
    if (!file.r2_key) return
    const url = await getDownloadUrl(file.r2_key)
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl; a.download = file.nome
      document.body.appendChild(a); a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch {
      window.open(url, '_blank')
    }
  }

  const handleCreateCartella = async (nome, projectId, parentId) => {
    const { error } = await supabase.from('storage_cartelle').insert({
      nome, project_id: projectId || null,
      parent_id: parentId || null,
      created_by: profile.id,
    })
    if (error) { alert(`Errore creazione cartella: ${error.message}`); return }
    await fetchFiles()
  }

  const handleDeleteCartella = async (nome) => {
    const dbCart = cartelleDB.find(c => c.nome === nome && c.parent_id === (currentFolder?.id || null))
    if (!dbCart) return
    await supabase.from('storage_cartelle').delete().eq('id', dbCart.id)
    fetchFiles()
  }

  const handleSposta = async (file, projectId, cartellaName) => {
    const finalProjectId = projectId !== null ? (projectId || null) : file.project_id
    const { error } = await supabase.from('file_uploads').update({
      project_id: finalProjectId, cartella: cartellaName || null,
    }).eq('id', file.id)
    if (error) { alert(`Errore spostamento: ${error.message}`); return }
    await fetchFiles()
  }

  const handleBatchSposta = async (projectId, cartellaName) => {
    const ids = [...selectedFileIds]
    const { error } = await supabase.from('file_uploads').update({
      project_id: projectId || null, cartella: cartellaName || null,
    }).in('id', ids)
    if (error) { alert(`Errore spostamento: ${error.message}`); return }
    clearSelection()
    await fetchFiles()
  }

  const handleSpostaCartella = async (vecchioNome, nuovoNome, nuovoProjectId) => {
    const { error } = await supabase.from('file_uploads').update({
      cartella: nuovoNome || null, project_id: nuovoProjectId || null,
    }).eq('cartella', vecchioNome)
    if (error) { alert(`Errore: ${error.message}`); return }
    const dbCart = cartelleDB.find(c => c.nome === vecchioNome)
    if (dbCart) {
      await supabase.from('storage_cartelle').update({
        nome: nuovoNome, project_id: nuovoProjectId || null,
      }).eq('id', dbCart.id)
    }
    await fetchFiles()
  }

  const handleDropToFolder = async (fileId, cartellaPath) => {
    const { error } = await supabase.from('file_uploads').update({
      cartella: cartellaPath === '__root__' ? null : cartellaPath,
    }).eq('id', fileId)
    if (error) { alert(`Errore: ${error.message}`); return }
    await fetchFiles()
  }

  const handleSaveNote = async (fileId, note) => {
    await supabase.from('file_uploads').update({ note }).eq('id', fileId)
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, note } : f))
  }

  // ── Multi-select ──
  const toggleFileSelect = (e, id) => {
    e.stopPropagation()
    setSelectedFileIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
    if (!selectMode) setSelectMode(true)
  }
  const selectAllVisible = () => setSelectedFileIds(new Set(filtered.map(f => f.id)))
  const clearSelection = () => { setSelectedFileIds(new Set()); setSelectMode(false) }
  const handleDownloadSelected = async () => {
    const toDownload = filtered.filter(f => selectedFileIds.has(f.id))
    for (const f of toDownload) {
      await handleDownload(f)
      await new Promise(r => setTimeout(r, 500))
    }
    clearSelection()
  }

  const handleDelete = async (file) => {
    if (!canDelete) return
    if (confirmDelete !== file.id) { setConfirmDelete(file.id); return }
    const { data: versioni } = await supabase
      .from('file_uploads').select('id, r2_key')
      .or(`id.eq.${file.id},file_padre_id.eq.${file.id}`)
    for (const v of (versioni || [])) {
      if (v.r2_key) await deleteFile(v.r2_key).catch(() => {})
      await supabase.from('file_uploads').delete().eq('id', v.id)
    }
    setConfirmDelete(null)
    fetchFiles()
  }

  // ── Cartelle da mostrare nel livello corrente ──
  const cartelleDaMostrare = (() => {
    if (currentFolder?.id) {
      // Dentro una cartella DB: mostra le sue sottocartelle
      return cartelleDB.filter(c => c.parent_id === currentFolder.id)
    }
    // A root: mostra le cartelle radice + eventuali cartelle virtuali (da file, senza record DB)
    const dbRoots = cartelleDB.filter(c => !c.parent_id)
    const dbRootNames = new Set(dbRoots.map(c => c.nome))
    const virtualNames = [...new Set(files.map(f => {
      if (!f.cartella) return null
      const topLevel = f.cartella.split('/')[0]
      return topLevel
    }).filter(Boolean))].filter(name => !dbRootNames.has(name))
    return [
      ...dbRoots,
      ...virtualNames.map(nome => ({ id: null, nome, parent_id: null, project_id: null }))
    ].sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
  })()

  // Filtro file
  const filtered = files.filter(f => {
    let matchCartella
    if (showRootOnly) {
      matchCartella = !f.cartella
    } else if (!currentFolder) {
      matchCartella = true
    } else {
      matchCartella = f.cartella === currentFolderPath
    }
    const q = query.toLowerCase()
    const matchQuery = !q || f.nome?.toLowerCase().includes(q) || f.progetto?.titolo?.toLowerCase().includes(q)
    return matchCartella && matchQuery
  })

  const totalSize = files.reduce((acc, f) => acc + (f.dimensione || 0), 0)
  const selectedLabel = selectedProject === 'all'
    ? 'Tutti i progetti'
    : (progetti.find(p => p.id === selectedProject)?.titolo || '—')

  const hasRootFiles = files.some(f => !f.cartella)
  const conteggioCartella = (cartellaObj) => {
    if (!cartellaObj.id) return files.filter(f => f.cartella === cartellaObj.nome).length
    const path = currentFolderPath
      ? `${currentFolderPath}/${cartellaObj.nome}`
      : cartellaObj.nome
    return files.filter(f => f.cartella === path).length
  }

  const isInsideFolder = currentFolder || showRootOnly

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Storage</h1>
          <p className="text-gray-500 text-sm mt-1">
            {files.length} file · {fmt(totalSize)}
            <span className="ml-2 text-gray-600">· Cloudflare R2</span>
          </p>
        </div>
        {canUpload && tab === 'files' && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNuovaCartella(true)}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
              <FolderPlus size={15} />
              {currentFolder ? 'Sottocartella' : 'Nuova cartella'}
            </button>
            <button onClick={() => inputRef.current?.click()} disabled={uploadQueue.length > 0}
              className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
              <Upload size={15} />
              {uploadQueue.length > 0 ? 'Caricamento...' : 'Carica file'}
            </button>
            <input ref={inputRef} type="file" multiple className="hidden"
              onChange={e => openUploadModal(Array.from(e.target.files))} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 mb-6 w-fit">
        <button onClick={() => setTab('files')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'files' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
          <Folder size={14} /> File
        </button>
        <button onClick={() => setTab('info')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'info' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
          <Info size={14} /> Info
        </button>
      </div>

      {tab === 'info' && (
        <InfoTab files={files} canDelete={canDelete} onDelete={handleDelete} confirmDelete={confirmDelete} />
      )}

      {tab === 'files' && <>

      <ProgressBar items={uploadQueue} />

      {uploadError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4 flex items-start gap-3">
          <span className="text-red-400 text-sm flex-1">{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-300 flex-shrink-0"><X size={16} /></button>
        </div>
      )}

      {/* Filtri */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca file..."
            className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>
        <div className="relative" ref={menuRef}>
          <button onClick={() => setShowProjectMenu(v => !v)}
            className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-gray-300 hover:border-gray-700 transition-colors whitespace-nowrap">
            {selectedProject !== 'all' && (
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: progetti.find(p => p.id === selectedProject)?.colore || '#6d28d9' }} />
            )}
            {selectedLabel}
            <ChevronDown size={14} className="text-gray-500" />
          </button>
          {showProjectMenu && (
            <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden z-20 min-w-48 shadow-xl">
              {[{ id: 'all', titolo: 'Tutti i progetti', colore: null }, ...progetti].map(p => (
                <button key={p.id} onClick={() => { setSelectedProject(p.id); setShowProjectMenu(false); navigateToRoot() }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors ${selectedProject === p.id ? 'bg-violet-600/20 text-violet-300' : 'text-gray-300 hover:bg-gray-800'}`}>
                  {p.colore && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />}
                  {p.titolo}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Drop zone */}
      {canUpload && !isInsideFolder && (
        <div
          onDragOver={e => { e.preventDefault(); setDropActive(true) }}
          onDragLeave={() => setDropActive(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-5 text-center mb-5 cursor-pointer transition-colors ${dropActive ? 'border-violet-500 bg-violet-600/10' : 'border-gray-800 hover:border-gray-700'}`}>
          <Upload size={18} className="mx-auto text-gray-600 mb-1.5" />
          <p className="text-gray-500 text-sm">Trascina qui i file o clicca per selezionarli</p>
          <p className="text-gray-600 text-xs mt-0.5">Potrai scegliere progetto e cartella prima di caricare</p>
        </div>
      )}

      {/* Cartelle */}
      {!loading && !query && (
        <div className="mb-5">
          {/* Breadcrumb */}
          {(isInsideFolder) && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              <button onClick={navigateToRoot} className="text-xs text-gray-500 hover:text-white transition-colors">Storage</button>
              {folderNav.map((f, idx) => (
                <span key={f.id || idx} className="flex items-center gap-1.5">
                  <ChevronRight size={12} className="text-gray-700" />
                  {idx < folderNav.length - 1 ? (
                    <button onClick={() => navigateTo(idx)} className="text-xs text-gray-500 hover:text-white transition-colors">{f.nome}</button>
                  ) : (
                    <span className="text-xs text-white font-medium">{f.nome}</span>
                  )}
                </span>
              ))}
              {showRootOnly && (
                <span className="flex items-center gap-1.5">
                  <ChevronRight size={12} className="text-gray-700" />
                  <span className="text-xs text-white font-medium">Senza cartella</span>
                </span>
              )}
            </div>
          )}

          {/* Torna indietro */}
          {isInsideFolder && (
            <button onClick={navigateBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-3">
              <ArrowLeft size={13} /> Indietro
            </button>
          )}

          {/* Griglia cartelle */}
          {(cartelleDaMostrare.length > 0 || (!isInsideFolder && hasRootFiles)) && (
            <>
              {!isInsideFolder && (
                <p className="text-xs text-gray-600 font-medium uppercase tracking-wider mb-3">Cartelle</p>
              )}
              {isInsideFolder && cartelleDaMostrare.length > 0 && (
                <p className="text-xs text-gray-600 font-medium uppercase tracking-wider mb-3">Sottocartelle</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
                {cartelleDaMostrare.map(c => {
                  const count = conteggioCartella(c)
                  const isDBOnly = c.id && cartelleDB.some(d => d.id === c.id) && count === 0
                  const isDragOver = dragOverFolder === (currentFolderPath ? `${currentFolderPath}/${c.nome}` : c.nome)
                  const fullPath = currentFolderPath ? `${currentFolderPath}/${c.nome}` : c.nome
                  const hasSubfolders = c.id && cartelleDB.some(d => d.parent_id === c.id)
                  return (
                    <div key={c.id || c.nome} className="relative group/folder"
                      onDragOver={e => { e.preventDefault(); setDragOverFolder(fullPath) }}
                      onDragLeave={() => setDragOverFolder(null)}
                      onDrop={e => { e.preventDefault(); setDragOverFolder(null); const id = e.dataTransfer.getData('fileId'); if (id) handleDropToFolder(id, fullPath) }}>
                      <button onClick={() => navigateInto(c)}
                        className={`w-full bg-gray-900 border rounded-xl p-4 flex flex-col items-center gap-2 transition-colors group ${isDragOver ? 'border-amber-400 bg-amber-400/10' : 'border-gray-800 hover:border-amber-500/40'}`}>
                        <FolderOpen size={28} className={`transition-colors ${isDragOver ? 'text-amber-300' : count === 0 ? 'text-amber-400/50 group-hover:text-amber-400' : 'text-amber-400 group-hover:text-amber-300'}`} />
                        <span className="text-white text-xs font-medium truncate w-full text-center">{c.nome}</span>
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-600">
                          <span>{count === 0 ? 'Vuota' : `${count} file`}</span>
                          {hasSubfolders && <><span>·</span><span>sottocartelle</span></>}
                        </div>
                      </button>

                      {/* Azioni cartella */}
                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover/folder:opacity-100 transition-all">
                        {/* Copia link */}
                        {c.id && (
                          <button onClick={e => { e.stopPropagation(); handleCopyFolderLink(c) }}
                            title="Copia link cartella"
                            className={`w-5 h-5 rounded-full flex items-center justify-center shadow transition-all ${copiedFolderId === c.id ? 'bg-emerald-600' : 'bg-gray-800 hover:bg-violet-600'}`}>
                            {copiedFolderId === c.id ? <Check size={9} className="text-white" /> : <Link2 size={9} className="text-gray-400 hover:text-white" />}
                          </button>
                        )}
                        {/* Rinomina */}
                        {canUpload && c.id && (
                          <button onClick={e => { e.stopPropagation(); setSpostaCartella({ nome: c.nome, project_id: c.project_id }) }}
                            title="Rinomina cartella"
                            className="w-5 h-5 bg-gray-800 hover:bg-amber-600 rounded-full flex items-center justify-center shadow transition-all">
                            <FolderInput size={9} className="text-gray-400 hover:text-white" />
                          </button>
                        )}
                        {/* Elimina (solo se vuota e DB) */}
                        {isDBOnly && canUpload && (
                          <button onClick={e => { e.stopPropagation(); handleDeleteCartella(c.nome) }}
                            title="Elimina cartella vuota"
                            className="w-5 h-5 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center shadow transition-all">
                            <X size={9} className="text-gray-400 hover:text-white" />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}

                {/* Cartella "Senza cartella" — solo a root */}
                {!isInsideFolder && hasRootFiles && (
                  <div className="relative group/folder"
                    onDragOver={e => { e.preventDefault(); setDragOverFolder('__root__') }}
                    onDragLeave={() => setDragOverFolder(null)}
                    onDrop={e => { e.preventDefault(); setDragOverFolder(null); const id = e.dataTransfer.getData('fileId'); if (id) handleDropToFolder(id, '__root__') }}>
                    <button onClick={() => { setShowRootOnly(true); setFolderNav([]) }}
                      className={`w-full bg-gray-900 border rounded-xl p-4 flex flex-col items-center gap-2 transition-colors group ${dragOverFolder === '__root__' ? 'border-gray-500 bg-gray-500/10' : 'border-gray-800 hover:border-gray-600'}`}>
                      <Folder size={28} className={`transition-colors ${dragOverFolder === '__root__' ? 'text-gray-300' : 'text-gray-600 group-hover:text-gray-400'}`} />
                      <span className="text-gray-400 text-xs font-medium">Senza cartella</span>
                      <span className="text-gray-600 text-[10px]">{files.filter(f => !f.cartella).length} file</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Lista file */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 && !(!isInsideFolder && cartelleDaMostrare.length > 0) ? (
        <div className="text-center py-16">
          <p className="text-gray-600 text-sm">
            {query ? 'Nessun risultato.' : isInsideFolder ? 'Nessun file in questa cartella.' : 'Nessun file ancora.'}
          </p>
          {canUpload && isInsideFolder && (
            <button onClick={() => inputRef.current?.click()}
              className="mt-3 flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors mx-auto">
              <Upload size={13} /> Carica qui
            </button>
          )}
        </div>
      ) : filtered.length > 0 ? (
        <>
          {/* Header lista file */}
          <div className="flex items-center gap-3 mb-2 px-1">
            <button onClick={selectedFileIds.size === filtered.length ? clearSelection : selectAllVisible}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors">
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${selectedFileIds.size === filtered.length && filtered.length > 0 ? 'bg-violet-600 border-violet-600' : 'border-gray-600'}`}>
                {selectedFileIds.size === filtered.length && filtered.length > 0 && <Check size={10} className="text-white" />}
              </div>
              {selectedFileIds.size > 0 ? `${selectedFileIds.size} selezionati` : 'Seleziona tutto'}
            </button>
            {selectedFileIds.size > 0 && (
              <button onClick={clearSelection} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">Deseleziona</button>
            )}
            {/* Modalità selezione */}
            {!selectMode && selectedFileIds.size === 0 && (
              <button onClick={() => setSelectMode(true)}
                className="ml-auto text-xs text-gray-600 hover:text-gray-400 transition-colors flex items-center gap-1">
                <Check size={11} /> Seleziona
              </button>
            )}
            {selectMode && selectedFileIds.size === 0 && (
              <button onClick={() => setSelectMode(false)}
                className="ml-auto text-xs text-gray-600 hover:text-gray-400 transition-colors">
                Annulla
              </button>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden divide-y divide-gray-800">
            {filtered.map(f => {
              const isSelected = selectedFileIds.has(f.id)
              return (
                <div key={f.id}
                  draggable={canUpload}
                  onDragStart={e => { e.dataTransfer.setData('fileId', f.id); e.dataTransfer.effectAllowed = 'move' }}
                  onClick={() => { if (selectMode) { toggleFileSelect({ stopPropagation: () => {} }, f.id) } else setPreviewFile(f) }}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-800/40 transition-colors group cursor-pointer ${isSelected ? 'bg-violet-600/5' : ''}`}>
                  {/* Checkbox */}
                  <div
                    onClick={e => toggleFileSelect(e, f.id)}
                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                      isSelected ? 'bg-violet-600 border-violet-600' : 'border-gray-700 group-hover:border-gray-500'
                    } ${(selectMode || selectedFileIds.size > 0) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {isSelected && <Check size={11} className="text-white" />}
                  </div>

                  <div className="flex-shrink-0">{tipoIcon(f.tipo)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white text-sm font-medium truncate">{f.nome}</p>
                      {f.versione > 1 && (
                        <span className="text-xs bg-violet-600/20 text-violet-400 px-1.5 py-0.5 rounded flex-shrink-0">v{f.versione}</span>
                      )}
                      {f.visibilita === 'personale' && (
                        <span title="Privato — visibile solo a te" className="flex-shrink-0">
                          <Lock size={10} className="text-gray-600" />
                        </span>
                      )}
                      {f.visibilita === 'specifici' && (
                        <span title="Condiviso con persone specifiche" className="flex-shrink-0">
                          <Users size={10} className="text-violet-500" />
                        </span>
                      )}
                      {f.note && (
                        <span title={f.note} className="flex-shrink-0">
                          <MessageSquare size={11} className="text-violet-400" />
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-2">
                      <span>{fmt(f.dimensione)}</span>
                      {f.progetto && (<><span>·</span><span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.progetto.colore || '#6d28d9' }} />{f.progetto.titolo}</span></>)}
                      {f.cartella && (<><span>·</span><span className="flex items-center gap-1 text-amber-600"><Folder size={10} /> {f.cartella}</span></>)}
                      <span>·</span>
                      <span>{new Date(f.created_at).toLocaleDateString('it-IT')}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setPreviewFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors" title="Anteprima">
                      <Maximize2 size={15} />
                    </button>
                    {/* Nota */}
                    <button onClick={() => setNoteFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors" title="Note">
                      <MessageSquare size={15} />
                    </button>
                    {f.versione > 1 && (
                      <button onClick={() => setVersioniFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-amber-400 hover:bg-gray-700 transition-colors" title="Cronologia versioni">
                        <History size={15} />
                      </button>
                    )}
                    <button onClick={() => handleDownload(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-violet-400 hover:bg-gray-700 transition-colors">
                      <Download size={15} />
                    </button>
                    {canUpload && (
                      <button onClick={() => setSpostaFile(f)} className="p-1.5 rounded-lg text-gray-500 hover:text-amber-400 hover:bg-gray-700 transition-colors" title="Sposta">
                        <FolderInput size={15} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(f)}
                        className={`p-1.5 rounded-lg transition-colors ${confirmDelete === f.id ? 'text-red-400 bg-red-500/10' : 'text-gray-500 hover:text-red-400 hover:bg-gray-700'}`}>
                        {confirmDelete === f.id ? <X size={15} /> : <Trash2 size={15} />}
                      </button>
                    )}
                  </div>
                  {confirmDelete === f.id && (
                    <button onClick={e => { e.stopPropagation(); handleDelete(f) }}
                      className="text-xs text-red-400 border border-red-500/30 rounded-lg px-2 py-1 hover:bg-red-500/10 transition-colors whitespace-nowrap">
                      Conferma
                    </button>
                  )}
                  {!canUpload && !canDelete && (
                    <button onClick={e => { e.stopPropagation(); handleDownload(f) }} className="p-1.5 rounded-lg text-gray-600 hover:text-violet-400 transition-colors opacity-0 group-hover:opacity-100">
                      <Download size={15} />
                    </button>
                  )}
                  <ChevronRight size={14} className="text-gray-700 flex-shrink-0 opacity-0 group-hover:opacity-100" />
                </div>
              )
            })}
          </div>
        </>
      ) : null}

      {/* Barra selezione multipla */}
      {selectedFileIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-700 rounded-2xl px-5 py-3 flex items-center gap-4 shadow-2xl z-30">
          <span className="text-white text-sm font-medium">{selectedFileIds.size} file selezionati</span>
          <div className="w-px h-5 bg-gray-700" />
          <button onClick={() => setShowBatchSposta(true)}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
            <FolderInput size={14} /> Sposta
          </button>
          <button onClick={handleDownloadSelected}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
            <Download size={14} /> Scarica tutti
          </button>
          <button onClick={clearSelection} className="p-1.5 text-gray-400 hover:text-white transition-colors" title="Deseleziona">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Modali */}
      {pendingFiles && (
        <UploadModal
          files={pendingFiles}
          progetti={progetti}
          cartelleDB={cartelleDB}
          defaultCartella={currentFolderPath}
          onClose={() => setPendingFiles(null)}
          onUpload={uploadFiles}
        />
      )}
      {versioniFile && (
        <VersioniPanel file={versioniFile} onClose={() => setVersioniFile(null)} onDownload={handleDownload} />
      )}
      {previewFile && (
        <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} onDownload={handleDownload} />
      )}
      {noteFile && (
        <FileNoteModal file={noteFile} onClose={() => setNoteFile(null)} onSave={handleSaveNote} />
      )}
      {spostaFile && (
        <SpostaModal file={spostaFile} progetti={progetti} cartelleDB={cartelleDB} onClose={() => setSpostaFile(null)} onSposta={handleSposta} />
      )}
      {showNuovaCartella && (
        <NuovaCartellaModal
          progetti={progetti}
          defaultProject={selectedProject}
          parentId={currentFolder?.id || null}
          parentNome={currentFolder?.nome || null}
          onClose={() => setShowNuovaCartella(false)}
          onCreate={handleCreateCartella}
        />
      )}
      {showBatchSposta && (
        <BatchSpostaModal count={selectedFileIds.size} progetti={progetti} cartelleDB={cartelleDB} onClose={() => setShowBatchSposta(false)} onSposta={handleBatchSposta} />
      )}
      {spostaCartella && (
        <SpostaCartellaModal
          cartellaName={spostaCartella.nome}
          progetti={progetti}
          currentProjectId={spostaCartella.project_id}
          onClose={() => setSpostaCartella(null)}
          onSposta={handleSpostaCartella}
        />
      )}

      {!isAdmin && (
        <p className="text-gray-700 text-xs mt-4 text-right">
          {canUpload ? 'Puoi caricare e scaricare file.' : 'Puoi solo visualizzare e scaricare i file condivisi.'}
        </p>
      )}

      </>}
    </div>
  )
}

export default Storage
